import {useRef, useEffect, useMemo} from 'react';

import {useGetAiSearchResults} from '../graphql/hooks/use-get-ai-search-results';

type Mode = 'search' | 'commands';

const QUESTION_WORDS = [
    'how',
    'what',
    'where',
    'when',
    'why',
    'who',
    'which',
    'show',
    'find',
    'go',
    'change',
    'update',
    'open',
];

export function useAiSearch(mode: Mode, debouncedSearchValue: string) {
    const {executeAiSearch, aiResults, isAiLoading} = useGetAiSearchResults();
    const lastAiQueryRef = useRef<string | null>(null);

    const isNaturalLanguage = useMemo(() => {
        if (!debouncedSearchValue) {
            return false;
        }
        const words = debouncedSearchValue.trim().split(/\s+/);
        const hasQuestionWord = QUESTION_WORDS.some((word) => words[0]?.toLowerCase() === word);

        return words.length > 3 || hasQuestionWord;
    }, [debouncedSearchValue]);

    useEffect(() => {
        if (debouncedSearchValue !== lastAiQueryRef.current) {
            lastAiQueryRef.current = null;
        }
    }, [debouncedSearchValue]);

    useEffect(() => {
        if (mode !== 'commands') {
            return;
        }
        if (!isNaturalLanguage || !debouncedSearchValue) {
            return;
        }
        if (lastAiQueryRef.current === debouncedSearchValue) {
            return;
        }

        const timer = setTimeout(() => {
            lastAiQueryRef.current = debouncedSearchValue;
            executeAiSearch(debouncedSearchValue);
        }, 500);

        return () => clearTimeout(timer);
    }, [mode, isNaturalLanguage, debouncedSearchValue, executeAiSearch]);

    return {aiResults, isAiLoading};
}
